import { isNodeSelection } from "@tiptap/core";

import { matrixSelectionJson } from "@/components/expression/utils/matrixSelection";
import { copyTiptapContent } from "@/components/tiptap/utils/copyTiptapContent";
import { duplicateCurrentTiptapSelection } from "@/components/tiptap/utils/duplicateTiptapContent";
import { pasteNodes } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { NodeIdMapping } from "@/components/tiptap/utils/copyTiptapContent";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/core";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";

type MathCursorInfo = {
  wrapperNodeId: string;
  /** Index of cursor.node among all InlineNodeModel descendants of the wrapper */
  inlineNodeIndex: number;
  /** Cursor index within that node */
  cursorIndex: number;
};

/**
 * Get all InlineNodeModel descendants of a node (depth-first order).
 */
function getInlineDescendants(node: WrapperNodeModel): InlineNodeModel[] {
  return node.descendants.filter((n): n is InlineNodeModel => n.is(InlineNodeModel));
}

/**
 * Capture current math cursor position if cursor is inside a math node.
 */
function captureMathCursor(doc: DocumentModel): MathCursorInfo | undefined {
  if (!cursorIsActive(doc.cursor) || !doc.cursor.node) {
    return undefined;
  }

  const wrapperNode = doc.cursor.node.ancestors.find((n) => n.is(WrapperNodeModel));
  if (!wrapperNode?.is(WrapperNodeModel)) {
    return undefined;
  }

  // Find cursor.node's index among all InlineNodeModel descendants
  const inlineNodes = getInlineDescendants(wrapperNode);
  const inlineNodeIndex = inlineNodes.indexOf(doc.cursor.node);

  if (inlineNodeIndex === -1) {
    return undefined;
  }

  return {
    wrapperNodeId: wrapperNode.id,
    inlineNodeIndex,
    cursorIndex: doc.cursor.index ?? 0,
  };
}

function duplicateTiptapNodeAtPos(
  doc: DocumentModel,
  editor: Editor,
  node: ProsemirrorNode,
  pos: number,
  options?: { cursorOffsetInNode?: number; mathCursor?: MathCursorInfo },
) {
  const docSize = editor.state.doc.content.size;
  const rawTargetPos = pos + node.nodeSize;
  const targetPos = Math.min(rawTargetPos, docSize);
  const nodeIdMapping = copyTiptapContent(node, doc, doc, { atPos: targetPos });

  // Position cursor after duplication - use requestAnimationFrame to wait for React render
  requestAnimationFrame(() => {
    restoreCursorAfterDuplication(doc, editor, targetPos, nodeIdMapping, options);
  });
}

function restoreCursorAfterDuplication(
  doc: DocumentModel,
  editor: Editor,
  targetPos: number,
  nodeIdMapping: NodeIdMapping,
  options?: { cursorOffsetInNode?: number; mathCursor?: MathCursorInfo },
) {
  // Guard: check if editor is still mounted
  if (editor.isDestroyed) return;

  // If cursor was in math, restore it to the corresponding position in the new math node
  if (options?.mathCursor) {
    const { wrapperNodeId, inlineNodeIndex, cursorIndex } = options.mathCursor;
    const newWrapperNodeId = nodeIdMapping.get(wrapperNodeId);

    if (newWrapperNodeId) {
      const newWrapperNode = doc.nodes.get(newWrapperNodeId);
      if (newWrapperNode?.is(WrapperNodeModel)) {
        // Find the corresponding InlineNodeModel in the new wrapper
        const newInlineNodes = getInlineDescendants(newWrapperNode);
        const targetNode = newInlineNodes[inlineNodeIndex];

        if (targetNode) {
          // Wait a frame for the new nodes to be ready in the document
          requestAnimationFrame(() => {
            if (!editor.isDestroyed) {
              doc.cursor.moveTo(targetNode, cursorIndex, { forceFocus: true });
            }
          });
          return;
        }
      }
    }
  }

  // Otherwise, position cursor at the same relative position on the new line (for text)
  if (options?.cursorOffsetInNode !== undefined) {
    const newCursorPos = targetPos + options.cursorOffsetInNode;
    editor.commands.setTextSelection(newCursorPos);
    editor.commands.focus();
  }
}

// This is used when user clicks on the duplicate button
export function duplicateTiptapLineAtPos(
  doc?: DocumentModel,
  editor?: Editor,
  pos?: number,
): boolean {
  if (!doc || !editor || pos === undefined) return false;

  const nodeToCopy = editor.state.doc.nodeAt(pos);
  if (!nodeToCopy) return false;

  // Capture math cursor position if cursor is in math
  const mathCursor = captureMathCursor(doc);

  duplicateTiptapNodeAtPos(doc, editor, nodeToCopy, pos, { mathCursor });
  return true;
}

// This is used when user presses Cmd+D
export function duplicateCurrentTiptapLine(doc: DocumentModel, editor: Editor): boolean {
  const { selection } = editor.state;
  const { $from } = selection;
  const { depth: fromDepth } = $from;

  let nodeToCopy: ProsemirrorNode | null = null;
  let nodePos: number | null = null;

  // When the user has selected a block node (e.g. graph, image), use it directly.
  // Inline nodes (e.g. inlineExpression) still use the depth loop so we duplicate the line, not the node.
  if (isNodeSelection(selection) && selection.node.isBlock) {
    nodeToCopy = selection.node;
    nodePos = selection.from;
  } else {
    // Find a block node whose parent is the document/title container (same behavior as line-duplicate button).
    for (let depth = fromDepth; depth > 0; depth--) {
      const parent = $from.node(depth - 1);
      if (parent.type.name === "document" || parent.type.name === "documentTitleSchema") {
        nodeToCopy = $from.node(depth);
        nodePos = $from.before(depth);
        break;
      }
    }
  }

  if (!nodeToCopy || nodePos === null) {
    return false;
  }

  // Capture math cursor position if cursor is in math
  const mathCursor = captureMathCursor(doc);

  // Calculate cursor position relative to the start of the node (for text cursor fallback)
  const cursorOffsetInNode = selection.from - nodePos;

  duplicateTiptapNodeAtPos(doc, editor, nodeToCopy, nodePos, { cursorOffsetInNode, mathCursor });
  return true;
}

export function duplicateSelectedMath(doc: DocumentModel): boolean {
  const range = doc.selection.lastUpdatedInlineNodeRange;
  if (!range) return false;

  const matrixJson = matrixSelectionJson(doc);
  const selectedNodes = doc.selection.getSelectedNodes(range.node);

  if (!matrixJson && selectedNodes.length === 0) {
    return false;
  }

  doc.transact(() => {
    // Insert right after the selection end.
    const insertionIndex = range.to;
    doc.cursor.moveTo(range.node, insertionIndex);

    const nodesJson = [
      ...selectedNodes.map((n) => n.toJSON()),
      ...(matrixJson ? [matrixJson] : []),
    ];

    const pastedNodes = pasteNodes(doc, nodesJson);

    // Select all new duplicated nodes so they can be duplicated again
    const pastedCount = pastedNodes.filter(Boolean).length;
    if (pastedCount > 0) {
      doc.selection.setInlineNodeRangeBetween(
        range.node,
        insertionIndex,
        range.node,
        insertionIndex + pastedCount,
      );
    } else {
      doc.selection.resetSelection();
    }
    doc.editor.resetInput(true);
  }, "shortcut:Mod+KeyD:duplicateSelectedMath");

  return true;
}

export function duplicateEditorSelection(doc: DocumentModel, editor: Editor): boolean {
  const { selection } = editor.state;

  if (!selection.empty) {
    duplicateCurrentTiptapSelection(doc, editor.view, { insertAt: selection.to });
    return true;
  }

  return duplicateCurrentTiptapLine(doc, editor);
}

/**
 * Duplicate the current selection, switching on where focus/selection lives:
 * 1. Math input focused with a math-node/matrix selection → duplicate only those
 *    selected math nodes ({@link duplicateSelectedMath}).
 * 2. Cursor inside math but nothing selected → duplicate the current TipTap "line"
 *    block (the tiptap NodeSelection on the atom isn't a range to copy).
 * 3. Otherwise (focus in the TipTap document) → duplicate the editor selection the
 *    same way Cmd+D does ({@link duplicateEditorSelection}): a non-empty selection is
 *    copied and inserted right after its end, and a collapsed cursor duplicates the
 *    current line.
 */
export function duplicateSelection(doc: DocumentModel): boolean {
  if (
    cursorIsActive(doc.cursor) &&
    (doc.selection.hasSelectedNodes || doc.matrixSelection.currentSelection)
  ) {
    return duplicateSelectedMath(doc);
  }

  const { tiptap } = doc.editor;
  if (!tiptap) return false;

  if (cursorIsActive(doc.cursor)) {
    return duplicateCurrentTiptapLine(doc, tiptap);
  }

  return duplicateEditorSelection(doc, tiptap);
}
